import {
    List,
    Datagrid,
    TextField,
    EditButton,
    Edit,
    Create,
    SimpleForm,
    TextInput,
    required,
    Pagination,
    DateField,
    NumberField,
    NumberInput,
    ReferenceInput,
    SelectInput,
    useRecordContext,
    BooleanField,
    BooleanInput,
    FunctionField,
    Filter,
    useListContext,
    useNotify,
    useRefresh,
    ImageField,
} from 'react-admin';
import { 
    Chip, 
    Box, 
    Typography, 
    Card, 
    CardContent,
    Divider,
    Stack,
    Button,
    ButtonGroup
} from '@mui/material';
import { useState } from 'react';
import { getCategoryImageEndpoint } from '../config';

// Custom component for category image upload
const CategoryImageUpload = ({ label = "Category Image" }) => {
    const record = useRecordContext();
    const notify = useNotify();
    const refresh = useRefresh();
    const [uploading, setUploading] = useState(false);

    const handleUpload = async (file) => {
        if (!file || !record?.id) return;
        
        setUploading(true);
        const formData = new FormData();
        formData.append('image', file);
        
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(
                getCategoryImageEndpoint(record.id),
                {
                    method: 'POST',
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                    body: formData,
                }
            );
            
            if (!response.ok) {
                throw new Error('Upload failed');
            }
            
            const data = await response.json();
            notify(`${label} uploaded successfully`, { type: 'success' });
            refresh();
        } catch (error) {
            notify(`Failed to upload ${label.toLowerCase()}`, { type: 'error' });
        } finally {
            setUploading(false);
        }
    };

    const handleDelete = async () => {
        if (!record?.id || !record?.image) return;
        
        setUploading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(
                getCategoryImageEndpoint(record.id),
                {
                    method: 'DELETE',
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            
            if (!response.ok) {
                throw new Error('Delete failed');
            }
            
            notify(`${label} deleted successfully`, { type: 'success' });
            refresh();
        } catch (error) {
            notify(`Failed to delete ${label.toLowerCase()}`, { type: 'error' });
        } finally {
            setUploading(false);
        }
    };

    const currentImageUrl = record?.image;

    return (
        <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
                {label}
            </Typography>
            {currentImageUrl && (
                <Box sx={{ mb: 1 }}>
                    <img 
                        src={currentImageUrl} 
                        alt={`${label}`}
                        style={{ 
                            maxWidth: '150px', 
                            maxHeight: '100px',
                            objectFit: 'contain',
                            border: '1px solid #ddd',
                            borderRadius: '4px'
                        }} 
                    />
                    <br />
                    <Button 
                        variant="outlined" 
                        color="error" 
                        size="small" 
                        onClick={handleDelete}
                        disabled={uploading}
                        sx={{ mt: 1 }}
                    >
                        Delete Image
                    </Button>
                </Box>
            )}
            <input
                type="file"
                accept="image/png,image/jpeg,image/webp"
                onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleUpload(file);
                }}
                disabled={uploading}
                style={{ marginBottom: '8px' }}
            />
            {uploading && <Typography variant="caption">Uploading...</Typography>}
        </Box>
    );
};

// Custom component to display parent category name with visual hierarchy
const ParentField = () => {
    const record = useRecordContext();
    if (!record || !record.parent) {
        return <span style={{ color: '#666' }}>-</span>;
    }
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                {record.parent.name}
            </Typography>
        </Box>
    );
};

// Enhanced level field with better visual distinction
const LevelField = () => {
    const record = useRecordContext();
    if (!record) return null;
    
    const level = record.level;
    const isRoot = level === 1;
    
    return (
        <Chip 
            label={isRoot ? 'Root Category' : 'Subcategory'} 
            color={isRoot ? 'primary' : 'secondary'} 
            size="small"
            variant={isRoot ? 'filled' : 'outlined'}
            sx={{
                fontWeight: 'bold',
                ...(isRoot ? {} : { 
                    backgroundColor: 'rgba(156, 39, 176, 0.08)',
                    borderColor: '#9c27b0',
                    color: '#9c27b0'
                })
            }}
        />
    );
};

// Enhanced category name field with visual hierarchy
const CategoryNameField = () => {
    const record = useRecordContext();
    if (!record) return null;
    
    const isRoot = record.level === 1;
    
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {!isRoot && (
                <Typography 
                    variant="body2" 
                    sx={{ 
                        color: '#666', 
                        fontSize: '0.75rem',
                        fontWeight: 'normal'
                    }}
                >
                    ↳
                </Typography>
            )}
            <Typography 
                variant="body2" 
                sx={{ 
                    fontWeight: isRoot ? 'bold' : 'normal',
                    fontSize: isRoot ? '0.875rem' : '0.8rem',
                    color: isRoot ? '#1976d2' : '#333'
                }}
            >
                {record.name}
            </Typography>
        </Box>
    );
};

// Custom component to display slug with proper styling
const SlugField = () => {
    const record = useRecordContext();
    if (!record || !record.slug) {
        return <span style={{ color: '#999' }}>-</span>;
    }
    return (
        <Typography 
            variant="body2" 
            sx={{ 
                fontFamily: 'Monaco, Consolas, monospace', 
                fontSize: '0.75rem',
                backgroundColor: '#f5f5f5',
                padding: '2px 6px',
                borderRadius: '4px',
                color: '#666'
            }}
        >
            {record.slug}
        </Typography>
    );
};

// Filter component for the list
const CategoryFilter = (props) => (
    <Filter {...props}>
        <ReferenceInput 
            source="parent_id" 
            reference="categories" 
            filter={{ level: 1, is_active: true }}
            allowEmpty
        >
            <SelectInput optionText="name" emptyText="All Parents" />
        </ReferenceInput>
        <SelectInput
            source="is_active"
            choices={[
                { id: true, name: 'Active' },
                { id: false, name: 'Inactive' },
            ]}
            allowEmpty
            emptyText="All Status"
        />
    </Filter>
);

// Custom tab filters for view switching
const CategoryTabFilters = () => {
    const { filterValues, setFilters } = useListContext();
    const currentLevel = filterValues.level;

    const handleTabClick = (level) => {
        if (level === null) {
            // Show all categories
            const newFilters = { ...filterValues };
            delete newFilters.level;
            setFilters(newFilters, {}, false);
        } else {
            // Filter by level
            const newFilters = { ...filterValues, level };
            setFilters(newFilters, {}, false);
        }
    };

    return (
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                View:
            </Typography>
            <ButtonGroup variant="outlined" size="small">
                <Button
                    onClick={() => handleTabClick(null)}
                    variant={currentLevel === undefined ? 'contained' : 'outlined'}
                    sx={{ minWidth: '120px' }}
                >
                    All Categories
                </Button>
                <Button
                    onClick={() => handleTabClick(1)}
                    variant={currentLevel === 1 ? 'contained' : 'outlined'}
                    sx={{ minWidth: '120px' }}
                >
                    Root Categories
                </Button>
                <Button
                    onClick={() => handleTabClick(2)}
                    variant={currentLevel === 2 ? 'contained' : 'outlined'}
                    sx={{ minWidth: '120px' }}
                >
                    Subcategories
                </Button>
            </ButtonGroup>
        </Box>
    );
};

// Enhanced datagrid with better visualization
const CategoryDatagrid = () => {
    return (
        <Datagrid 
            sx={{
                '& .RaDatagrid-headerRow': {
                    backgroundColor: '#f8f9fa'
                },
                '& .RaDatagrid-row:nth-of-type(even)': {
                    backgroundColor: '#fafbfc'
                }
            }}
        >
            <TextField source="id" sortable={false} />
            <CategoryNameField label="Name" />
            <SlugField label="Slug" />
            <ImageField source="image" title="Image" label="Image" />
            <LevelField label="Level" />
            <ParentField label="Parent Category" />
            <BooleanField source="is_active" label="Active" />
            <NumberField source="ordering" />
            <DateField source="CreatedAt" showTime={true} label="Created At" />
            <EditButton />
        </Datagrid>
    );
};

// Custom list content component
const CategoryListContent = () => {
    return (
        <Box>
            <CategoryTabFilters />
            <CategoryDatagrid />
        </Box>
    );
};

// Main list component with proper structure
export const CategoryList = () => (
    <List
        pagination={<Pagination rowsPerPageOptions={[10, 25, 50]} />}
        perPage={25}
        sort={{ field: 'level', order: 'ASC' }}
        filters={<CategoryFilter />}
        filterDefaultValues={{}}
        disableAuthentication={false}
    >
        <CategoryListContent />
    </List>
);

// Enhanced parent category input with better UX
const ParentCategoryInput = () => {
    return (
        <Box>
            <ReferenceInput
                source="parent_id"
                reference="categories"
                label="Parent Category"
                filter={{ level: 1, is_active: true }}
                sort={{ field: 'name', order: 'ASC' }}
                allowEmpty
            >
                <SelectInput
                    optionText="name"
                    emptyText="None (Root Category)"
                    helperText="Select a parent category to create a subcategory. Leave empty for root category."
                />
            </ReferenceInput>
            <Typography 
                variant="caption" 
                color="text.secondary" 
                sx={{ mt: 1, display: 'block' }}
            >
                💡 Only root categories (Level 1) can be selected as parents. Maximum 2 levels supported.
            </Typography>
        </Box>
    );
};

// Enhanced edit form with better hierarchy information
export const CategoryEdit = () => (
    <Edit>
        <SimpleForm>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
            <TextInput source="name" validate={[required()]} />
                <TextInput 
                    source="slug" 
                    helperText="URL-friendly version of the name. Leave empty to auto-generate." 
                />
            </Box>
            
            <TextInput 
                source="description" 
                multiline 
                rows={4} 
                fullWidth 
                sx={{ mb: 2 }}
            />
            
            <ParentCategoryInput />
            
            <CategoryImageUpload />
            
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2, mt: 2 }}>
                <BooleanInput source="is_active" label="Active" defaultValue={true} />
                <NumberInput source="ordering" defaultValue={0} />
                <FunctionField 
                    label="Current Level" 
                    render={record => {
                        const level = record.level;
                        const label = level === 1 ? 'Root Category (Level 1)' : 'Subcategory (Level 2)';
                        return (
                            <Chip 
                                label={label} 
                                color={level === 1 ? 'primary' : 'secondary'} 
                                size="small" 
                                variant="outlined"
                            />
                        );
                    }}
                />
            </Box>
        </SimpleForm>
    </Edit>
);

// Enhanced create form
export const CategoryCreate = () => (
    <Create>
        <SimpleForm>
            <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                Create New Category
            </Typography>
            
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
            <TextInput source="name" validate={[required()]} />
                <TextInput 
                    source="slug" 
                    helperText="URL-friendly version of the name. Leave empty to auto-generate." 
                />
            </Box>
            
            <TextInput 
                source="description" 
                multiline 
                rows={4} 
                fullWidth 
                sx={{ mb: 2 }}
            />
            
            <ParentCategoryInput />
            
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mt: 2 }}>
                <BooleanInput source="is_active" label="Active" defaultValue={true} />
                <NumberInput source="ordering" defaultValue={0} />
            </Box>
            
            <Typography 
                variant="body2" 
                color="text.secondary" 
                sx={{ 
                    mt: 2, 
                    p: 2, 
                    backgroundColor: '#f8f9fa', 
                    borderRadius: 1,
                    border: '1px solid #e9ecef'
                }}
            >
                📋 <strong>Hierarchy Rules:</strong><br/>
                • Leave Parent Category empty to create a root category (Level 1)<br/>
                • Select a root category as parent to create a subcategory (Level 2)<br/>
                • Maximum 2 levels are supported
            </Typography>
        </SimpleForm>
    </Create>
); 